import { useRef, useEffect } from 'react'
import { Chart, registerables } from 'chart.js'
import { parseGraphData } from '@/lib/api'

Chart.register(...registerables)

interface TravelTimeChartProps {
  last90MinTT: string
  last90MinBase: string
  currentTT: number
  index: number
}

export function TravelTimeChart({ last90MinTT, last90MinBase, currentTT, index }: TravelTimeChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    if (chartRef.current) {
      chartRef.current.destroy()
    }

    const actualData = parseGraphData(last90MinTT)
    const baselineData = parseGraphData(last90MinBase)

    const allValues = [...actualData.filter((x): x is number => x !== null), ...baselineData.filter((x): x is number => x !== null)]
    const minVal = allValues.length > 0 ? Math.min(...allValues) : 0
    const maxVal = allValues.length > 0 ? Math.max(...allValues) : currentTT || 100

    const diff = maxVal - minVal
    const padding = diff < 60 ? (60 - diff) / 2 : 0
    const yMin = Math.max(0, minVal - padding)
    const yMax = maxVal + padding

    const labels = Array(180).fill('').map((_, i) => {
      const minutes = 90 - (i * 30 / 60)
      if ([90, 75, 60, 45, 30, 15].includes(minutes)) {
        return Math.floor(minutes).toString()
      }
      return ''
    })

    const ctx = canvasRef.current.getContext('2d')!
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Actual',
            data: actualData,
            borderColor: 'rgb(255, 0, 0)',
            backgroundColor: 'rgba(255, 0, 0, 0.1)',
            borderWidth: 1,
            pointRadius: 0,
            tension: 0,
            spanGaps: true,
          },
          {
            label: 'Baseline',
            data: baselineData,
            borderColor: 'rgb(0, 0, 255)',
            backgroundColor: 'rgba(0, 0, 255, 0.1)',
            borderWidth: 1,
            pointRadius: 0,
            tension: 0,
            spanGaps: true,
          },
        ],
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: {
          y: {
            display: true,
            min: yMin,
            max: yMax,
            ticks: {
              callback: (value) => {
                const v = Number(value)
                const min = Math.floor(v / 60)
                const sec = v - min * 60
                return min + 'm ' + sec + 's'
              },
              font: { size: 10 },
            },
            grid: { color: 'rgba(128, 128, 128, 0.2)' },
          },
          x: {
            display: true,
            ticks: { font: { size: 10 }, maxRotation: 0 },
            grid: { color: 'rgba(128, 128, 128, 0.2)' },
          },
        },
      },
    })

    return () => {
      chartRef.current?.destroy()
    }
  }, [last90MinTT, last90MinBase, currentTT, index])

  return <canvas ref={canvasRef} width={300} height={100} />
}
